/**
 * Resumable direct-to-GCS upload contract (video upload & processing, Phase 1).
 *
 * Two-phase: the client RESERVES an upload (server mints a GCS resumable
 * session + a PENDING artifact), uploads the bytes straight to GCS, then
 * FINALIZES (server verifies the landed object). These schemas are the wire
 * contract for the `/api/uploads/*` surface — the legacy multipart
 * `/api/attachments/upload` path is unaffected.
 */
import { z } from 'zod';
import { CAPTURE_METHODS, CAPTURE_SURFACES, isServerAssignedCaptureSurface, } from '../constants/capture';
import { PROCESSING_STATUS, PROCESSING_STATUS_VALUES, } from '../constants/artifact-status';
import { UPLOAD_INTENTS } from '../constants/fileProcessing';
import { naiveWallClockSchema } from '../utils/wire-time';
/** Hard byte cap for any attachment upload (500 MB). Single source of truth for
 * the backend reserve/finalize size gate, the Zod request schema, and the
 * frontend dropzone `maxSize` + advertised limit. */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;
/** Files one import or drop takes at a time (Create > Import, the workspace
 * canvas, onboarding). Past it, the first ones are kept and the rest skipped. */
export const MAX_FILES_PER_IMPORT = 20;
/** Files one attach takes at a time (a note's or protocol step's attachments).
 * Chat has its own per-message ceiling, kept in parity with the agent service. */
export const MAX_FILES_PER_ATTACH = 10;
/** Advertised duration cap (10 min). Enforced at finalize via ffprobe in
 * Phase 2; declared here as the single source of truth. */
export const MAX_VIDEO_DURATION_SECONDS = 600;
/**
 * Accepted file types for the ATTACH surfaces (Add attachment, Bird composer) —
 * a react-dropzone `Accept` map (mime → extensions). Single source of truth so
 * the dialog and the chat picker can't drift. The IMPORT surface intentionally
 * accepts ANY type (no filter) and does not use this. */
export const ATTACHMENT_ACCEPT_TYPES = {
    // Images
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'image/avif': ['.avif'],
    'image/svg+xml': ['.svg'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
    // Audio
    'audio/mpeg': ['.mp3'],
    'audio/wav': ['.wav'],
    'audio/ogg': ['.ogg'],
    'audio/aac': ['.aac'],
    'audio/mp4': ['.m4a'],
    // Video (incl. the container types phones record)
    'video/webm': ['.webm'],
    'video/mp4': ['.mp4'],
    'video/quicktime': ['.mov'],
    'video/x-msvideo': ['.avi'],
    'video/x-matroska': ['.mkv'],
    'video/x-m4v': ['.m4v'],
    // Documents
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    // Text
    'text/plain': ['.txt'],
    'text/csv': ['.csv'],
    'text/markdown': ['.md'],
    // Archives
    'application/zip': ['.zip'],
    'application/x-rar-compressed': ['.rar'],
    'application/x-7z-compressed': ['.7z'],
    // Code / data. Keyed by nominal MIME, but the EXTENSION arrays are what
    // actually match in react-dropzone — .R/.Rmd/.jl/.do arrive with '' or
    // application/octet-stream on most operating systems.
    'application/json': ['.json', '.ipynb'],
    'application/xml': ['.xml'],
    'text/x-r-source': ['.r', '.R', '.Rmd', '.rmd', '.Rnw'],
    'text/x-python': ['.py'],
    'text/x-ruby': ['.rb'],
    'application/sql': ['.sql'],
    'application/x-sh': ['.sh', '.bash', '.zsh'],
    'text/x-julia': ['.jl'],
    'text/x-matlab': ['.m'],
    'text/x-stata': ['.do', '.ado'],
    'text/x-c': ['.c', '.h', '.cpp', '.hpp'],
    'text/x-java-source': ['.java'],
    'text/css': ['.css'],
    'application/x-yaml': ['.yaml', '.yml', '.toml'],
    'text/javascript': ['.js', '.jsx', '.ts', '.tsx'],
};
/**
 * Every accepted suffix, lower-cased and de-duplicated, derived from
 * ATTACHMENT_ACCEPT_TYPES so the extension view and the dropzone map cannot
 * drift. Consumed by `isAcceptedAttachment` and by the backend's multipart
 * fileFilter fallback.
 */
export const ATTACHMENT_ACCEPT_EXTENSIONS = Array.from(new Set(Object.values(ATTACHMENT_ACCEPT_TYPES)
    .flat()
    .map((extension) => extension.toLowerCase())));
/** Accept check for hand-rolled pickers (the Bird composer). Mirrors
 * react-dropzone semantics: a MIME-key match OR an extension match.
 *
 * The extension arm carries the load. Most operating systems register no MIME
 * type for .R/.Rmd/.jl/.do, so the browser reports '' or
 * application/octet-stream and a MIME-only check rejects a researcher's R
 * script outright. Case-insensitive, because `.R` and `.r` are both idiomatic.
 *
 * This is a PICKER filter, not a security boundary — the server-side
 * `attachmentFileFilter` is what actually admits bytes. */
export const isAcceptedAttachment = (fileName, mimeType) => {
    if (mimeType && Object.prototype.hasOwnProperty.call(ATTACHMENT_ACCEPT_TYPES, mimeType)) {
        return true;
    }
    const lowerName = fileName.toLowerCase();
    return ATTACHMENT_ACCEPT_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
};
/**
 * A GPS fix a client attached to a capture: the shape `ExifData.gps` stores.
 * (0, 0) is refused for the reason `utils/exif.ts` drops it: it is what a
 * device reports when it has no fix, not a place anyone recorded anything.
 */
export const clientCaptureGpsSchema = z
    .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    altitude: z.number().optional(),
})
    .refine((gps) => !(gps.latitude === 0 && gps.longitude === 0), {
    error: '(0, 0) is a missing fix, not a location',
});
/**
 * When the client says the capture was taken. ISO 8601, with an offset or as
 * local wall-clock time: the two forms the mobile app sends (device time with
 * its offset, or a library pick's EXIF DateTimeOriginal, which has none), and
 * the two forms `ExifData.captureDate` already holds. The offset-less form is
 * a wall clock whose zone is unknown, so it is never compared with an instant
 * (wire-time `naiveWallClockSchema`).
 */
export const clientCapturedAtSchema = naiveWallClockSchema;
/** An optional client idempotency key for reserve (the mobile outbox item id).
 * A bounded, id-shaped token: it is stored on the upload sidecar and matched
 * exactly, never shown. */
export const clientUploadIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/);
/** A capture's id: the app's per-capture request id (`live-photo-<ms>`, the
 * glasses SDK's `requestId`), called `captureId` on every Bower door. Bounded
 * and id-shaped like `clientUploadIdSchema`: stored in attachment metadata and
 * matched exactly, never shown and never sent to analytics. */
export const captureIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/);
export const uploadReserveRequestSchema = z.object({
    /** The owning entity the artifact nests under (e.g. 'artifact.protocol'). */
    parentEntityType: z.string().min(1).max(120),
    parentEntityId: z.string().min(1).max(120),
    fileName: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(255),
    /** Optional user-typed display title. Blank titles follow
     * FILE_PROCESSING_CONFIG.titlePolicy: filename stores the original filename;
     * ai-generated stores null pending generation. (Single-file uploads only.) */
    title: z.string().max(255).optional(),
    /** Client-declared size — used only for the pre-upload quota check; the
     * authoritative size is re-read from GCS at finalize (never trusted). */
    declaredSize: z.int().positive().max(MAX_UPLOAD_BYTES),
    /** Intent: when true, note-convertible files (per FILE_PROCESSING_CONFIG) spawn
     * a note after upload (the Import flow). Default false = attach-only — the file
     * is stored (and video transcoded / equipment PDFs extracted in place) but no
     * note is created. Carried to dispatchPostUpload at finalize.
     *
     * `uploadIntent` supersedes this boolean when a caller declares one. This
     * field stays for clients that predate `uploadIntent` and still only speak
     * the boolean. */
    importAsNotes: z.boolean().optional(),
    /** The richer intent vocabulary (see `UPLOAD_INTENTS`), now declarable on the
     * resumable door too. When present it takes precedence over `importAsNotes`;
     * when absent, `importAsNotes` still resolves intent exactly as before. */
    uploadIntent: z.enum(UPLOAD_INTENTS).optional(),
    captureMethod: z.enum(CAPTURE_METHODS).optional(),
    /** Capture surface for the capture taxonomy; resolved server-side when
     * absent.
     *
     * BODY_DECLARABLE, not the full vocabulary. The three sibling doors
     * (`equipment.controller.ts`, `attachment.upload.controller.ts`,
     * `artifacts.controller.ts`) all deny-list the server-assigned surfaces, and
     * this one did not — so `POST /api/uploads/reserve` with
     * `captureSurface: "onboarding_seed"` was written verbatim, flipping
     * `isSeededExample` in the personalization engine and stamping `seeded` on
     * search results. Self-inflicted and in-workspace only, but two of the four
     * consumers this taxonomy exists to serve. `onboarding_import` IS
     * body-declarable, so the onboarding chain is unaffected. */
    captureSurface: z
        .enum(CAPTURE_SURFACES)
        .refine((surface) => !isServerAssignedCaptureSurface(surface), {
        error: 'captureSurface is server-assigned and may not be claimed by a request body',
    })
        .optional(),
    /** Requests speaker diarization for a meeting recording. Persisted on the
     * finalized attachment because async transcription reads attachment metadata. */
    multipleSpeakers: z.boolean().optional(),
    /** Note target for the Import flow — defaults to 'note' downstream. Set to
     * 'protocol' when importing into the Protocol Library so note-convertible
     * files become protocols, not notes. Only consulted when importAsNotes. */
    targetArtifactType: z.string().max(120).optional(),
    /** New-client cutover flag: when true, transcription + note-append for an
     * audio attachment under an `artifact.note` parent are SERVER-owned (the
     * `transcribe-note-audio` job, queued via startNoteAudioTranscription, called
     * from dispatchPostUpload).
     * Absent/false = the legacy client-driven flow, which stays the only writer
     * for those uploads — old bundles keep working with no double-append. Never
     * default this on. `noteCapture: 'transcript'` is the newer spelling of this
     * same intent (`wantsTranscript` in the upload controller treats them alike); both are
     * accepted so an old bundle sending only this flag keeps working. */
    serverTranscription: z.boolean().optional(),
    /** Opt-in for server-owned async processing of a note attachment, valid only
     * with a note parent (`artifact.note`): 'transcript' is the newer spelling of
     * `serverTranscription: true` (server-owned note-audio transcription);
     * 'ocr' starts the async `note-ocr` job (startNoteOcr, called from
     * dispatchPostUpload) instead of the synchronous `/ocr` + `/content/append`
     * path. Absent = the legacy client-driven flow for that capture. */
    noteCapture: z.enum(['transcript', 'ocr']).optional(),
    /** Which door a spreadsheet import came through, for the import's analytics
     * `source`. Only the Create dialog's Import sends it ('create'); absent means
     * 'upload'. An enum, not a string: it reaches PostHog, so the values stay
     * bounded. Stored on the sidecar only when sent. */
    importSource: z.enum(['create']).optional(),
    /** The artifact whose AUDIENCE the new one takes from birth: its explicit
     * privacy row and its grants, cloned in the same transaction as the insert.
     * Sent by the spreadsheet page when it saves a stopped save's unsaved edits
     * as a copy, so a private spreadsheet's copy is never readable by its
     * collection. The caller must hold EDIT on it, or the reserve is refused. */
    privacySourceArtifactId: z.string().uuid().optional(),
    /** Client-reported capture context, `{ capturedAt?, gps? }` (the mobile
     * app's `CaptureMetadata`). Accepted as `unknown` here and validated FIELD BY
     * FIELD by the server (`clientCapturedAtSchema`, `clientCaptureGpsSchema`): a
     * malformed field is dropped and logged, never a reason to fail the upload,
     * the same contract EXIF extraction keeps. Persisted into `metadata.exif`
     * at finalize, where a field the file's own EXIF provides wins. */
    capture: z.unknown().optional(),
    /** The Protocol Execution run active when this was captured. Verified at
     * reserve against (workspace, user, active or paused), exactly as the
     * multipart door verifies it; an unverifiable id is logged and dropped. The
     * `captured_during` edge is written at finalize. */
    executionRunId: z.guid().optional(),
    /** The run STEP it was captured on (the todo step id, a protocol stepKey),
     * when the client knows it. Only read with a verified `executionRunId`: the
     * `captured_during` edge then stamps that step rather than the run's step
     * when the upload lands, which a recording stopped by leaving its step has
     * already moved past. A step the run does not have is ignored. */
    executionStepId: z.string().min(1).max(120).optional(),
    /** Idempotency key (see `clientUploadIdSchema`). A repeated reserve with the
     * same key, from the same user in the same workspace within five hours,
     * returns the first upload with a fresh session while it is still
     * `uploading`, and 409 `UPLOAD_ALREADY_FINALIZED` (with `details.uploadId`)
     * once it has landed, instead of creating a second row. */
    clientUploadId: clientUploadIdSchema.optional(),
    /** The capture this file is a copy of (`captureIdSchema`). Unlike
     * `clientUploadId` (one outbox item) it names the CAPTURE, so every copy of
     * one photo or clip (the glasses' own upload, the BLE relay, the kept fetch)
     * shares it. Reserve refuses a second copy with 409 `CAPTURE_ALREADY_UPLOADED`
     * naming the attachment (`details.attachmentId`). */
    captureId: captureIdSchema.optional(),
});
/**
 * Attachment-metadata marker (`metadata.transcription`) for server-owned
 * note-audio transcription. Written only by the `transcribe-note-audio` job:
 * `queued`→`running`→`completed`|`failed`. `completed` is the job's idempotency
 * short-circuit, and the frontend attachment card renders `queued`/`running` as
 * "Transcribing…" and `failed` as a saved-but-untranscribed state.
 */
export const NOTE_TRANSCRIPTION_STATUSES = ['queued', 'running', 'completed', 'failed'];
/**
 * The transcription failures for which a retry is deterministically pointless.
 *
 * Both are DESIGNED terminal outcomes of the audio strategy, not errors: the
 * verdict is a property of the audio itself, so re-running the provider on the
 * same bytes reaches the same branch and re-bills a duration-priced call for
 * it. Quota, a provider 5xx, an append CONFLICT and the orphan sweep's timeout
 * are about the moment rather than the recording, and a retry genuinely
 * succeeds for those.
 *
 * This set is NOT "every code a retry cannot fix", and must not be read as one.
 * `transcribeNoteAudio` also reports the non-conflict `APPEND_*` codes, whose
 * own comment in that file says they "can never succeed on retry". Those still
 * get the retry-inviting copy: a KNOWN GAP deferred out of the change that
 * added this, not a judgement that a retry helps them. They are a different
 * copy problem — for an append failure the transcript SUCCEEDED and could not
 * be attached, so neither sentence below is true of it — and the set is OPEN
 * (`APPEND_${appendResult.error.code}`, anything but the conflict, including
 * `APPEND_unknown`), so covering it needs a rule, not two more entries here.
 *
 * `EXTRACTION_FAILED` is a trap in the OTHER direction and must NOT simply be
 * added. It is raised on any `nonRetryable` result, and that population is
 * MIXED: `extraction.service.ts` marks a Google API quota (RESOURCE_EXHAUSTED)
 * `nonRetryable` without the `QUOTA_EXHAUSTED:` prefix the job intercepts on,
 * so a quota block — which a retry genuinely does fix, once the quota
 * resets — lands on `EXTRACTION_FAILED` too. Moving it in here wholesale would
 * withdraw the retry offer from a recoverable outcome, which is the same
 * CR-057 error as the one this set exists to fix, only inverted: a wrong
 * "do not retry" strands a reading that would have succeeded.
 *
 * The classification lives here, next to the marker that carries the code, so
 * the notification and the attachment row cannot drift into disagreeing about
 * whether a given recording is worth retrying. The COPY stays local to each
 * surface: they have different shapes (a title plus a message vs a single row
 * subtitle), and the two reasons need different wording rather than one shared
 * "terminal" line. Saying "no speech" about a rejected fabrication would blame
 * the user's microphone for our model inventing text, which is exactly the
 * conflation the split exists to prevent.
 */
export const NOTE_TRANSCRIPT_TERMINAL_CODES = [
    /** The recording carried no speech to transcribe. */
    'NO_SPEECH_DETECTED',
    /** A transcript came back but was implausible, so it was thrown away. */
    'TRANSCRIPT_REJECTED_IMPLAUSIBLE',
];
/** Narrows an arbitrary marker/notification code to the terminal set. */
export function isTerminalTranscriptCode(code) {
    return code !== undefined && NOTE_TRANSCRIPT_TERMINAL_CODES.includes(code);
}
/**
 * The `note-ocr` slot error codes a retry cannot fix, for the same reason and
 * on the same terms as `NOTE_TRANSCRIPT_TERMINAL_CODES`: the classification is
 * shared so the notification, the attachment row and the activity glyph agree,
 * and each surface keeps its own copy. `ATTACHMENT_GONE` is not here: the job
 * never announces it (the photo is no longer on the note).
 */
export const NOTE_OCR_TERMINAL_CODES = [
    /** The photo was read and carried no text. */
    'NO_TEXT_FOUND',
    /** The workspace's text-extraction allowance is spent for this period. */
    'AI_TEXT_EXTRACTION_QUOTA_EXHAUSTED',
    /** The file is not an image OCR can read. */
    'INVALID_FILE_TYPE',
    /** The image is damaged: the OCR provider could not decode it. */
    'IMAGE_UNDECODABLE',
    /** The image is too large to send to the OCR model, even recompressed. */
    'IMAGE_TOO_LARGE',
];
/** Narrows an arbitrary slot/notification code to the terminal OCR set. */
export function isTerminalOcrCode(code) {
    return code !== undefined && NOTE_OCR_TERMINAL_CODES.includes(code);
}
export const uploadReserveResponseSchema = z.object({
    /** The PENDING artifact's id, also the finalize handle. */
    uploadId: z.guid(),
    /** GCS resumable session URI the browser PUTs chunks to. Bearer credential,
     * owner-only, TLS-only, never logged. */
    sessionUri: z.url(),
    /** The server-chosen object key (under `workspaces/{ws}/uploads/`). */
    objectKey: z.string().min(1),
    /** 'spreadsheet' when this upload will become a native spreadsheet. */
    artifactType: z.string().optional(),
    /** Bounded code: why a CSV/XLSX stays a file. */
    intakeNote: z.string().optional(),
});
export const uploadFinalizeResponseSchema = z.object({
    artifactId: z.guid(),
    /** Finalize has exactly two successful outcomes. Any other wire value is a
     * contract failure and must not reach callers as a usable upload. */
    processingStatus: z.enum([PROCESSING_STATUS.READY, PROCESSING_STATUS.TRANSCODING]),
    /** Actual bytes as read back from GCS. */
    sizeBytes: z.int().nonnegative(),
    artifactType: z.string().optional(),
});
export const uploadStatusResponseSchema = z.object({
    artifactId: z.guid(),
    processingStatus: z.enum(PROCESSING_STATUS_VALUES),
});
//# sourceMappingURL=upload.schema.js.map